import {
  DATE_FORMAT_RANGE_FNS,
  FilterKeys,
  GetTimetableResponse,
  GroupMin,
  GroupType,
  groupTypeMap,
  ModifyTimetableRequest,
  PeriodTypeEnum,
  TimetableStructure,
} from '@schooly/api';
import { TimeTablingItemColor } from '@schooly/style';
import { format, parseISO } from 'date-fns';

import {
  SlotAllocationsMap,
  SlotRoomMap,
  TimetableAllocation,
  TimetableAllocationDetailed,
  TimetableForm,
  TimetableGroup,
  TimetableRoom,
} from './types';

export const SCHOOL_YEAR_ID_PARAM = 'school_year_id';
export const STRUCTURE_ID_PARAM = 'structure_id';

export const PERIOD_TYPE_COLOR_PRESETS: Record<PeriodTypeEnum, TimeTablingItemColor> = {
  [PeriodTypeEnum.Lesson]: 'main',
  [PeriodTypeEnum.Break]: 'darkGreen',
  [PeriodTypeEnum.Activity]: 'moss',
  [PeriodTypeEnum.Registration]: 'dijon',
} as Record<PeriodTypeEnum, TimeTablingItemColor>;

export type TimetableGridFilters = Partial<{
  [FilterKeys.AgeGroup]: string[];
  [FilterKeys.Subject]: string[];
}>;

export function transformTimetableToForm(timetable: GetTimetableResponse): TimetableForm {
  const slotAllocations: SlotAllocationsMap = {};

  for (const { time_slot_id, allocations } of timetable.slot_allocations) {
    slotAllocations[time_slot_id] = allocations;
  }

  return {
    name: timetable.name,
    slotAllocations,
    groups: timetable.groups,
    rooms: timetable.rooms,
  };
}

export function buildSlotRoomMap(
  slotAllocations: SlotAllocationsMap,
  rooms: TimetableRoom[],
): SlotRoomMap {
  const roomNameById = new Map(rooms.map((r) => [r.id, r.name]));
  const result: SlotRoomMap = {};

  for (const [timeSlotId, allocations] of Object.entries(slotAllocations)) {
    const groupRooms: Record<string, string> = {};

    for (const { group_id, room_id } of allocations) {
      const name = room_id ? roomNameById.get(room_id) : undefined;
      if (name) groupRooms[group_id] = name;
    }

    if (Object.keys(groupRooms).length > 0) {
      result[timeSlotId] = groupRooms;
    }
  }

  return result;
}

export function transformFormToRequest(data: TimetableForm): ModifyTimetableRequest['data'] {
  return {
    name: data.name,
    slot_allocations: Object.entries(data.slotAllocations)
      .filter(([, allocations]) => allocations.length > 0)
      .map(([timeSlotId, allocations]) => ({
        time_slot_id: timeSlotId,
        allocations,
      })),
  };
}

export function isLessonsEmpty(slotAllocations: TimetableForm['slotAllocations']) {
  return Object.values(slotAllocations).every((groups) => groups.length === 0);
}

export function hasPublishedTimetable(
  structure: TimetableStructure | GetTimetableResponse['structure'],
  schoolYearId: string,
) {
  return structure.timetable_usage.some(
    (usage) => usage.school_year_id === schoolYearId && usage.has_published,
  );
}

export type GroupForTimetable = TimetableGroup & { default_room_id?: string | null };

export function normalizeGroup(group: GroupMin & { group_type: GroupType }): GroupForTimetable {
  return {
    id: group.id,
    name: group.name,
    student_count: group.student_count,
    type: groupTypeMap[group.group_type],
    subject: group.subject
      ? {
          id: group.subject.id,
          name: group.subject.name,
        }
      : null,
    age_groups: group.age_groups,
    default_room_id: group.default_room_id,
  };
}

export function groupMatchesTimetableFilters(
  group: TimetableGroup,
  filters: TimetableGridFilters,
): boolean {
  const selectedAgeGroups = filters.age_group ?? [];
  const selectedSubjects = filters.subject ?? [];

  const matchesAgeGroup =
    !selectedAgeGroups.length ||
    (group.age_groups ?? []).some((ageGroup) => selectedAgeGroups.includes(ageGroup.id));

  const matchesSubject =
    !selectedSubjects.length || (!!group.subject && selectedSubjects.includes(group.subject.id));

  return matchesAgeGroup && matchesSubject;
}

export function splitGroups<T>(groups: T[], visibleCount: number) {
  return {
    visible: groups.slice(0, visibleCount),
    hiddenCount: Math.max(0, groups.length - visibleCount),
  };
}

export function getTimetableStatus(publishedAt: string | null, isPastYear: boolean) {
  if (isPastYear) {
    return {
      status: 'inactive',
      labelId: 'school-timetables-Inactive',
      color: 'text.secondary',
    };
  }

  if (publishedAt) {
    return {
      status: 'published',
      labelId: 'status-Published',
      color: 'success.main',
      publishedDate: format(parseISO(publishedAt), DATE_FORMAT_RANGE_FNS),
    };
  }

  return {
    status: 'not_published',
    labelId: 'status-NotPublished',
    color: 'warning.main',
  };
}

type GetTimetableAllocationsDetailedProps = {
  allocations: TimetableAllocation[];
  groups: TimetableGroup[];
  rooms: TimetableRoom[];
};

export function getTimetableAllocationsDetailed({
  allocations,
  groups,
  rooms,
}: GetTimetableAllocationsDetailedProps) {
  return groups.reduce<TimetableAllocationDetailed[]>((acc, group) => {
    const allocation = allocations.find((a) => a.group_id === group.id);
    if (allocation) {
      acc.push({
        group,
        room: allocation.room_id ? (rooms.find((r) => r.id === allocation.room_id) ?? null) : null,
      });
    }
    return acc;
  }, []);
}

type GetRoomWarningMessageProps = {
  groupsByRoom: Map<string, { name: string; id: string }[]>;
  roomId: string;
  groupId: string;
};

export function getRoomGroupNames({ groupsByRoom, roomId, groupId }: GetRoomWarningMessageProps) {
  const groupsWithSameRoom = groupsByRoom.get(roomId) ?? [];
  return (
    groupsWithSameRoom
      .filter((g) => g.id !== groupId)
      .map((g) => `"${g.name}"`)
      .join(', ') ?? undefined
  );
}

export function getGroupsByRoom(allocations: TimetableAllocationDetailed[]) {
  const map = new Map<string, { name: string; id: string }[]>();
  for (const { room, group } of allocations) {
    if (!room) continue;
    const names = map.get(room.id) ?? [];
    names.push({ name: group.name, id: group.id });
    map.set(room.id, names);
  }
  return map;
}
